import * as Hogan from 'hogan.js';
import {CoreDI, inject, injectable, System} from '@gongxigames/framework/core';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {ResourceManager} from '@gongxigames/framework/slot/services/ResourcesManager';
import {Module, setVisibleRewind, View} from '@gongxigames/framework/slot';
import { KeyboardKeyCodes } from '@gongxigames/framework/core/types/IKeyboard';

export interface Coords {
    top: number;
    left: number;
}

export const RewindModuleEvents = {
    SET_SLOW_SPEED: Symbol('SET_SLOW_SPEED'),
    SET_FORWARD_SPEED: Symbol('SET_FORWARD_SPEED')
} as const;

@injectable()
export class RewindModule extends Module<View> {
    public static readonly NAME: symbol = Symbol('RewindModule');

    public static readonly FORWARD: number = KeyboardKeyCodes.F;
    public static readonly PAUSE: number = KeyboardKeyCodes.P;
    public static readonly REWIND: number = KeyboardKeyCodes.R;
    public static readonly SLOW: number = KeyboardKeyCodes.S;
    public static readonly FORWARD_SPEED: number = 10;
    public static readonly SLOW_SPEED: number = 0.1;

    @inject(CoreDI.System)
    protected _system!: System;

    @inject(DI.ResourceManager)
    protected _resourceManager!: ResourceManager;

    private _rewind!: HTMLElement;
    private _pauseBtn!: HTMLElement;
    private _rewindBtn!: HTMLElement;
    private _playBtn!: HTMLElement;
    private _forwardBtn!: HTMLElement;
    private _slowBtn!: HTMLElement;
    private moveAtListener: (...args: any[]) => void = this.moveAt.bind(this, 0, 0);
    private keyDownListener: (e: KeyboardEvent) => void = this.onKeyDown.bind(this);

    public init(): void {

        super.init();

        this._resourceManager.loadTemplate('rewind')
            .then(this.onLoadedTemplate.bind(this));
    }

    public destroy(): void {
        super.destroy();

        document.removeEventListener('mousemove', this.moveAtListener);
        document.removeEventListener('keydown', this.keyDownListener);

        if (this.rewind && this.rewind.parentNode) {
            this.rewind.remove();
        }
    }

    protected setupViews(): void {}

    protected setupStates(): void {}

    protected setupEvents(): void {
        this.on(RewindModuleEvents.SET_FORWARD_SPEED, this.setForwardSpeed);
        this.on(RewindModuleEvents.SET_SLOW_SPEED, this.setSlowSpeed);
    }

    protected onLoadedTemplate(templateString: string): void {
        this.createRewindContainer(templateString);
        this.initElements();
        this.initDragNDrop();
        this.initButtons();

        if (!this.isPlatformDesktop()) {
            this._rewind.style.left = '10px';
            this._rewind.style.top = '45px';
            this._rewind.style.bottom = 'unset';
        }

        if (this.isHidden()) {
            setVisibleRewind(false);
        }
    }

    protected createRewindContainer(template: string): void {
        const html = Hogan.compile(template).render({});

        document.getElementsByTagName('canvas')[0].insertAdjacentHTML('afterend', html);
    }

    protected initElements(): void {
        this.rewind = document.getElementById('rewindContainer')!;
        this._pauseBtn = document.getElementById('pause')!;
        this._rewindBtn = document.getElementById('rewind')!;
        this._playBtn = document.getElementById('play')!;
        this._forwardBtn = document.getElementById('forward')!;
        this._slowBtn = document.getElementById('slow')!;
    }

    protected initDragNDrop(): void {
        if (this.isPlatformDesktop()) {
            this.initKeyboardDragNDrop();
        } else {
            this.initTouchDragNDrop();
        }
    }

    protected initKeyboardDragNDrop(): void {
        this.rewind.addEventListener('pointerdown', (e) => {
            // draggable by left button only
            if (e.which !== 1) {
                return false;
            }

            const coords = this.getCoords(this.rewind);
            const shiftX = e.pageX - coords.left;
            const shiftY = e.pageY - coords.top;

            this.moveAtListener = this.moveAt.bind(this, shiftX, shiftY);

            this.rewind.style.left = e.pageX - shiftX + 'px';
            this.rewind.style.top = e.pageY - shiftY + 'px';

            document.addEventListener('mousemove', this.moveAtListener);
            this.rewind.addEventListener('mouseup', () => document.removeEventListener('mousemove', this.moveAtListener));
        });

        this.rewind.addEventListener('ondragstart', () => {
            return false;
        });
    }

    protected initTouchDragNDrop(): void {
        const rewindContainer = document.getElementById('rewindContainer')!;
        let shiftX: number;
        let shiftY: number;

        rewindContainer.addEventListener('touchstart', (e: any) => {
            const touch = e.targetTouches[0];
            const coords = this.getCoords(this.rewind);

            shiftX = touch.clientX - coords.left;
            shiftY = touch.clientY - coords.top;
        });

        rewindContainer.addEventListener('touchend', (e: any) => {
            const touch = e.changedTouches[0];
            const coordX = touch.clientX - shiftX;
            const coordY = touch.clientY - shiftY;

            if (this.isPositionOutsidetheScreen(coordX, coordY)) {
                return;
            }

            this.rewind.style.left = coordX + 'px';
            this.rewind.style.top = coordY + 'px';
        });
    }

    protected getCoords(elem: HTMLElement): Coords {
        const item = elem.getBoundingClientRect();

        return {
            top: item.top + pageYOffset,
            left: item.left + pageXOffset
        };
    }

    protected isPositionOutsidetheScreen(x: number, y: number): boolean {
        const html = document.documentElement;

        return x < 0 || y < 0 || x > html.clientWidth - this.rewind.clientWidth || y > html.clientHeight - this.rewind.clientHeight;
    }

    protected moveAt(shiftX: number, shiftY: number, e: any): void {
        const coordX = e.pageX - shiftX;
        const coordY = e.pageY - shiftY;

        if (this.isPositionOutsidetheScreen(coordX, coordY)) {
            return;
        }

        this.rewind.style.left = coordX + 'px';
        this.rewind.style.top = coordY + 'px';
    }

    protected initButtons(): void {
        this._pauseBtn.onclick = () => this.toggleButtons();
        this._playBtn.onclick = () => this.toggleButtons();
        this._rewindBtn.onclick = () => this._system.nextFrame();
        this._forwardBtn.onclick = () => this.toggleTickerForwardSpeed();
        this._slowBtn.onclick = () => this.toggleTickerSlowSpeed();

        document.addEventListener('keydown', this.keyDownListener);
    }

    private onKeyDown(e: KeyboardEvent): void {
        if (e.keyCode === RewindModule.PAUSE) {
            this.toggleButtons();
        } else if (e.keyCode === RewindModule.REWIND) {
            this._system.nextFrame();
        } else if (e.keyCode === RewindModule.FORWARD) {
            this.toggleTickerForwardSpeed();
        } else if (e.keyCode === RewindModule.SLOW) {
            this.toggleTickerSlowSpeed();
        }
    }

    protected toggleButtons(): void {
        this._pauseBtn.classList.toggle('active');
        this._playBtn.classList.toggle('active');
        this._forwardBtn.classList.toggle('active');
        this._system.togglePause();
    }

    protected toggleTickerForwardSpeed(): void {
        this._system.ticker.speed = this._system.ticker.speed !== 1 ? 1 : RewindModule.FORWARD_SPEED;
    }

    protected toggleTickerSlowSpeed(): void {
        this._system.ticker.speed = this._system.ticker.speed !== 1 ? 1 : RewindModule.SLOW_SPEED;
    }

    protected setForwardSpeed(): void {
        this._system.ticker.speed = RewindModule.FORWARD_SPEED;
    }

    protected setSlowSpeed(): void {
        this._system.ticker.speed = RewindModule.SLOW_SPEED;
    }

    protected isHidden(): boolean {
        return !this.settings.get('rewindVisible');
    }

    public get rewind(): HTMLElement {
        return this._rewind;
    }

    public set rewind(value: HTMLElement) {
        this._rewind = value;
    }
}
